/**
 * ModeToggle Component
 * 
 * Task 5.1: Create ModeToggle component
 * Requirements: 3.1
 * 
 * Provides a toggle button to switch between View Mode and Edit Mode.
 * Updates the store state and provides visual indication of current mode.
 */

import { ToggleButton, ToggleButtonGroup, Box, Typography, Tooltip } from '@mui/material';
import { Visibility, Edit } from '@mui/icons-material';
import { useTimetableDesignerStore } from '../../store/timetableDesignerStore';

export interface ModeToggleProps {
  disabled?: boolean;
}

export function ModeToggle({ disabled = false }: ModeToggleProps) {
  const editMode = useTimetableDesignerStore((state) => state.editMode);
  const setEditMode = useTimetableDesignerStore((state) => state.setEditMode);

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'view' | 'edit' | null) => {
    if (newMode !== null) {
      setEditMode(newMode === 'edit');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        Mode:
      </Typography>
      <ToggleButtonGroup
        value={editMode ? 'edit' : 'view'}
        exclusive
        onChange={handleModeChange}
        disabled={disabled}
        size="small"
        aria-label="View or Edit mode"
      >
        <ToggleButton value="view" aria-label="View mode">
          <Tooltip title="View Mode - Read-only display with standard layout">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Visibility fontSize="small" />
              <Typography variant="body2">View</Typography>
            </Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="edit" aria-label="Edit mode">
          <Tooltip title="Edit Mode - Full-screen interactive editing">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Edit fontSize="small" />
              <Typography variant="body2">Edit</Typography>
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
